import { AudioOutlined, BookOutlined, FileTextOutlined, MoreOutlined, PlaySquareOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Col, Empty, Row, Skeleton, Space, Typography } from 'antd';
import { DesignProps } from 'api/DesignApi';
import { Inbox, SharedArtifactSummary, SharedLifeStorySummary, useGetSharedWithMeOverviewQuery } from 'api/InboxApi';
import PreviewInboxModalComponent, {
  PREVIEW_INBOX_MODAL_NAME
} from 'components/Inbox/PreviewInboxModalComponent/PreviewInboxModalComponent';
import ContainerComponent from 'components/Tool/ContainerComponent/ContainerComponent';
import Footer from 'pages/Client/Footer/Footer';
import Header from 'pages/Client/Header/Header';
import { CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { openModal } from 'store/modalSlice';
import { useAppDispatch } from 'store/store';
import { isEternalOnlyBranding, toAlphaColor } from 'utils/branding';
import dayjs from 'utils/dayjs';
import { getLifeStoryResponseText } from 'utils/lifeStory';
import { getFullNameOrEmail } from 'utils/utils';
import './SharedWithMe.css';

const { Paragraph, Text, Title } = Typography;

type SharedLifeStoryCard = {
  id: string;
  inviteId: string;
  title: string;
  sectionTitle?: string;
  previewText: string;
  promptCount: number;
  responseCount: number;
  senderName?: string;
  sharedAt?: string | null;
  designProps?: DesignProps;
};

const DEFAULT_PRIMARY_COLOR = 'var(--color-primary, #5449dc)';
const DEFAULT_PRIMARY_LIGHTER = 'var(--color-primary-lighter, #eeedfb)';

const isEmptySharedState = (status?: number | string) => {
  const numericStatus = Number(status);
  return numericStatus === 400 || numericStatus === 404 || numericStatus === 410;
};

const getAccentStyles = (designProps?: DesignProps) => {
  const useItemBranding = Boolean(designProps?.colorPrimary) && !isEternalOnlyBranding(designProps?.companyName);
  const accentColor = useItemBranding ? designProps?.colorPrimary || DEFAULT_PRIMARY_COLOR : DEFAULT_PRIMARY_COLOR;
  const accentColorLight = useItemBranding
    ? designProps?.colorPrimaryLighter || DEFAULT_PRIMARY_LIGHTER
    : DEFAULT_PRIMARY_LIGHTER;

  return {
    '--shared-accent-color': accentColor,
    '--shared-accent-color-light': accentColorLight,
    '--shared-accent-color-border': useItemBranding
      ? toAlphaColor(designProps?.colorPrimary || '', 0.12)
      : 'rgba(84, 73, 220, 0.12)',
    '--shared-accent-color-border-strong': useItemBranding
      ? toAlphaColor(designProps?.colorPrimary || '', 0.14)
      : 'rgba(84, 73, 220, 0.14)',
    '--shared-accent-color-hover': useItemBranding
      ? toAlphaColor(designProps?.colorPrimary || '', 0.03)
      : 'rgba(84, 73, 220, 0.03)'
  } as CSSProperties;
};

const getInitialsFromText = (value?: string | null) => {
  const words = (value || '').trim().split(/\s+/).filter(Boolean).slice(0, 2);

  return words.map((word) => word.charAt(0).toUpperCase()).join('') || 'SW';
};

const getSharedDateText = (value?: string | null, fallback = 'Recently shared') => {
  if (!value) {
    return fallback;
  }

  const formatted = dayjs(value);
  return formatted.isValid() ? formatted.format('MMM D, YYYY') : fallback;
};

const getPossessiveLabel = (value?: string | null, suffix?: string) => {
  const baseValue = value?.trim();
  if (!baseValue) {
    return suffix || 'Shared item';
  }

  if (!suffix) {
    return baseValue;
  }

  const normalizedSuffix = suffix.toLowerCase();
  if (baseValue.toLowerCase().includes(normalizedSuffix)) {
    return baseValue;
  }

  return `${baseValue}${baseValue.endsWith('s') ? "'" : "'s"} ${suffix}`;
};

const getMessageTypeDetails = (type: Inbox['message']['type']) => {
  switch (type) {
    case 'audio':
      return { icon: <AudioOutlined />, label: 'Audio' };
    case 'video':
      return { icon: <PlaySquareOutlined />, label: 'Video' };
    default:
      return { icon: <FileTextOutlined />, label: 'Text' };
  }
};

const getLifeStoryDisplayName = (lifeStory?: SharedLifeStorySummary['lifeStory']) => {
  return (
    lifeStory?.heroTitle?.trim() ||
    lifeStory?.fullName?.trim() ||
    [lifeStory?.firstName, lifeStory?.lastName].filter(Boolean).join(' ').trim() ||
    'Shared Life Story'
  );
};

const getLifeStoryCardPreview = (item: SharedLifeStorySummary, index: number): SharedLifeStoryCard => {
  const lifeStory = item.lifeStory;
  let previewText = '';
  let sectionTitle: string | undefined;
  let promptCount = 0;
  let responseCount = 0;

  (lifeStory.sections || []).forEach((section, sectionIndex) => {
    const resolvedSectionTitle = section?.title?.trim() || `Story section ${sectionIndex + 1}`;
    promptCount += section?.prompts?.length || 0;
    const promptResponses = (section?.prompts || []).flatMap((prompt) => {
      const responseList = prompt?.responses || [];
      const singleResponse = prompt?.response ? [prompt.response] : [];

      return [...responseList, ...singleResponse];
    });

    [...(section?.responses || []), ...promptResponses].forEach((response) => {
      const text = getLifeStoryResponseText(response)?.trim();
      const hasAudio = Boolean(response?.audioUrl || response?.audioDownloadUrl);
      if (!text && !hasAudio) {
        return;
      }

      responseCount += 1;

      if (!previewText) {
        previewText = text || 'Includes a shared audio response.';
        sectionTitle = resolvedSectionTitle;
      }
    });
  });

  return {
    id: lifeStory.id || item.inviteId || `shared-life-story-${index}`,
    inviteId: item.inviteId,
    title: getPossessiveLabel(getLifeStoryDisplayName(lifeStory), 'Life Story'),
    sectionTitle,
    previewText: previewText || 'Open this shared Life Story to view it in full.',
    promptCount,
    responseCount,
    senderName: item.senderName || getLifeStoryDisplayName(lifeStory),
    sharedAt: item.sharedAt,
    designProps: item.designProps
  };
};

const getArtifactSummaryDescription = (artifact: SharedArtifactSummary) => {
  if (artifact.description?.trim()) {
    return artifact.description.trim();
  }

  if (artifact.itemCount > 1) {
    return `${artifact.itemCount} shared artifacts in this collection.`;
  }

  return 'Shared artifact collection item.';
};

const getArtifactCardTitle = (artifact: SharedArtifactSummary) => {
  return artifact.senderName
    ? getPossessiveLabel(artifact.senderName, 'Artifacts')
    : artifact.title || 'Shared artifacts';
};

const getArtifactCardSubtitle = (artifact: SharedArtifactSummary) => {
  return artifact.senderName ? `Shared by ${artifact.senderName}` : getArtifactSummaryDescription(artifact);
};

const getMessagesEmptyState = (status?: number | string, messages?: Inbox[]) =>
  isEmptySharedState(status) || (!!messages && messages.length === 0);

const getSectionAccentStyles = <T extends { designProps?: DesignProps }>(items: T[]) => {
  const brandedItem = items.find((item) => item.designProps);
  return getAccentStyles(brandedItem?.designProps);
};

export function SharedWithMe() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { data, isLoading, error, refetch } = useGetSharedWithMeOverviewQuery();

  const status = (error as { status?: number | string } | undefined)?.status;
  const messages = data?.messages?.items ?? [];
  const artifacts = data?.artifacts?.items ?? [];
  const lifeStories = data?.lifeStories?.items ?? [];
  const recentMessages = messages.slice(0, 5);
  const recentArtifacts = artifacts.slice(0, 4);
  const lifeStoryItems = lifeStories.map((lifeStory, index) => getLifeStoryCardPreview(lifeStory, index)).slice(0, 3);
  const messageSectionAccentStyles = getSectionAccentStyles(recentMessages);
  const artifactSectionAccentStyles = getSectionAccentStyles(recentArtifacts);
  const lifeStorySectionAccentStyles = getSectionAccentStyles(lifeStoryItems);
  const hasAnySharedContent = messages.length > 0 || artifacts.length > 0 || lifeStoryItems.length > 0;
  const showMessagesEmptyState = !isLoading && getMessagesEmptyState(status, messages);
  const showArtifactEmptyState = !isLoading && (isEmptySharedState(status) || (!!data && artifacts.length === 0));
  const showLifeStoryEmptyState = !isLoading && (isEmptySharedState(status) || (!!data && lifeStoryItems.length === 0));
  const showErrorState = Boolean(error) && !isEmptySharedState(status);

  const openMessagePreview = (item: Inbox) => {
    dispatch(openModal({ name: PREVIEW_INBOX_MODAL_NAME, data: { id: item.id, inboxItem: item } }));
  };

  return (
    <div className="shared-with-me layout-full-height">
      <Header />

      <ContainerComponent className="layout-main-content">
        <div className="shared-with-me__content">
          <div className="shared-with-me__hero">
            <Title level={1}>Shared With Me</Title>
            <Paragraph>Content your loved ones have shared with you.</Paragraph>
          </div>

          {!isLoading && !showErrorState && !hasAnySharedContent && (
            <section className="shared-with-me__section" aria-labelledby="shared-with-me-empty-heading">
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={<span id="shared-with-me-empty-heading">Nothing has been shared with you yet.</span>}
              />
            </section>
          )}

          <section
            className="shared-with-me__section"
            aria-labelledby="shared-with-me-messages-heading"
            style={messageSectionAccentStyles}
          >
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              <div className="shared-with-me__section-header">
                <div className="shared-with-me__section-title-wrap">
                  <span className="shared-with-me__section-icon" aria-hidden="true">
                    <AudioOutlined />
                  </span>
                  <Title level={2} id="shared-with-me-messages-heading">
                    Messages
                  </Title>
                </div>
              </div>

              {isLoading && <Skeleton active paragraph={{ rows: 3 }} />}

              {!isLoading && showErrorState && !showMessagesEmptyState && (
                <Alert
                  type="error"
                  showIcon
                  message="We couldn’t load your shared messages right now."
                  action={
                    <Button size="small" onClick={() => refetch()}>
                      Refresh
                    </Button>
                  }
                />
              )}

              {showMessagesEmptyState && <Empty description="No shared messages yet." />}

              {!isLoading && !error && recentMessages.length > 0 && (
                <>
                  <div className="shared-with-me__message-list">
                    {recentMessages.map((item) => {
                      const senderName = getFullNameOrEmail(item.message.user);
                      const typeDetails = getMessageTypeDetails(item.message.type);

                      return (
                        <button
                          type="button"
                          key={item.id}
                          className="shared-with-me__message-row"
                          onClick={() => openMessagePreview(item)}
                          style={getAccentStyles(item.designProps)}
                        >
                          <div className="shared-with-me__message-person">
                            <div className="shared-with-me__avatar">{getInitialsFromText(senderName)}</div>
                            <div>
                              <div className="shared-with-me__message-sender">{senderName}</div>
                            </div>
                          </div>

                          <div className="shared-with-me__message-name">{item.message.name || 'Shared message'}</div>

                          <div className="shared-with-me__message-type">
                            <span className="shared-with-me__message-type-icon" aria-hidden="true">
                              {typeDetails.icon}
                            </span>
                            <span>{typeDetails.label}</span>
                          </div>

                          <div className="shared-with-me__message-date">
                            {getSharedDateText(item.createdAt, 'Recently shared')}
                          </div>

                          <div className="shared-with-me__message-actions">
                            <span className="shared-with-me__outline-button">Open</span>
                            <span className="shared-with-me__more-icon" aria-hidden="true">
                              <MoreOutlined />
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  <PreviewInboxModalComponent />
                </>
              )}
            </Space>
          </section>

          <section
            className="shared-with-me__section"
            aria-labelledby="shared-with-me-artifacts-heading"
            style={artifactSectionAccentStyles}
          >
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              <div className="shared-with-me__section-header">
                <div className="shared-with-me__section-title-wrap">
                  <span className="shared-with-me__section-icon" aria-hidden="true">
                    <FileTextOutlined />
                  </span>
                  <Title level={2} id="shared-with-me-artifacts-heading">
                    Artifacts
                  </Title>
                </div>
              </div>

              {isLoading && <Skeleton active paragraph={{ rows: 3 }} />}

              {!isLoading && showErrorState && !showArtifactEmptyState && (
                <Alert
                  type="error"
                  showIcon
                  message="We couldn’t load your shared artifacts right now."
                  action={
                    <Button size="small" onClick={() => refetch()}>
                      Refresh
                    </Button>
                  }
                />
              )}

              {showArtifactEmptyState && <Empty description="No shared artifacts yet." />}

              {!isLoading && !error && recentArtifacts.length > 0 && (
                <Row gutter={[16, 16]}>
                  {recentArtifacts.map((artifact) => (
                    <Col xs={24} md={12} xl={8} key={artifact.id}>
                      <Card
                        className="shared-with-me__summary-card"
                        variant="borderless"
                        style={getAccentStyles(artifact.designProps)}
                      >
                        <div className="shared-with-me__summary-card-header">
                          <div className="shared-with-me__avatar">
                            {getInitialsFromText(artifact.senderName || artifact.title)}
                          </div>
                          <div>
                            <Title level={4}>{getArtifactCardTitle(artifact)}</Title>
                            <Text className="shared-with-me__card-subtitle">{getArtifactCardSubtitle(artifact)}</Text>
                            <Text className="shared-with-me__card-date">{getSharedDateText(artifact.sharedAt)}</Text>
                          </div>
                        </div>

                        <Paragraph className="shared-with-me__card-copy">
                          {getArtifactSummaryDescription(artifact)}
                        </Paragraph>

                        <div className="shared-with-me__card-actions">
                          <Button
                            className="shared-with-me__action-button"
                            onClick={() => navigate(`/app/shared-with-me/artifacts/${artifact.shareId}`)}
                          >
                            View
                          </Button>
                        </div>
                      </Card>
                    </Col>
                  ))}
                </Row>
              )}
            </Space>
          </section>

          <section
            className="shared-with-me__section"
            aria-labelledby="shared-with-me-life-stories-heading"
            style={lifeStorySectionAccentStyles}
          >
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              <div className="shared-with-me__section-header">
                <div className="shared-with-me__section-title-wrap">
                  <span className="shared-with-me__section-icon" aria-hidden="true">
                    <BookOutlined />
                  </span>
                  <Title level={2} id="shared-with-me-life-stories-heading">
                    Life Stories
                  </Title>
                </div>
              </div>

              {isLoading && <Skeleton active paragraph={{ rows: 3 }} />}

              {!isLoading && showErrorState && !showLifeStoryEmptyState && (
                <Alert
                  type="error"
                  showIcon
                  message="We couldn’t load your shared Life Story right now."
                  action={
                    <Button size="small" onClick={() => refetch()}>
                      Refresh
                    </Button>
                  }
                />
              )}

              {showLifeStoryEmptyState && <Empty description="No shared Life Stories yet." />}

              {!isLoading && !error && lifeStoryItems.length > 0 && (
                <Row gutter={[16, 16]}>
                  {lifeStoryItems.map((item) => (
                    <Col xs={24} md={12} xl={8} key={item.id}>
                      <Card
                        className="shared-with-me__summary-card"
                        variant="borderless"
                        style={getAccentStyles(item.designProps)}
                      >
                        <div className="shared-with-me__summary-card-header">
                          <div className="shared-with-me__avatar">
                            {getInitialsFromText(item.senderName || item.title)}
                          </div>
                          <div>
                            <Title level={4}>{item.title}</Title>
                            <Text className="shared-with-me__card-subtitle">
                              {item.senderName ? `Shared by ${item.senderName}` : 'Shared Life Story'}
                            </Text>
                            <Text className="shared-with-me__card-date">{getSharedDateText(item.sharedAt)}</Text>
                          </div>
                        </div>

                        {item.sectionTitle && <Text className="shared-with-me__card-tag">{item.sectionTitle}</Text>}
                        <Paragraph className="shared-with-me__card-copy" ellipsis={{ rows: 3, expandable: false }}>
                          {item.previewText}
                        </Paragraph>

                        <div className="shared-with-me__card-actions shared-with-me__card-actions--split">
                          {!!(item.promptCount || item.responseCount) && (
                            <Text className="shared-with-me__card-metric">
                              <BookOutlined />{' '}
                              {`${item.promptCount || item.responseCount} ${(item.promptCount ? item.promptCount : item.responseCount) === 1 ? (item.promptCount ? 'prompt' : 'response') : item.promptCount ? 'prompts' : 'responses'}`}
                            </Text>
                          )}
                          <Button
                            className="shared-with-me__action-button shared-with-me__action-button--filled"
                            onClick={() => navigate(`/app/shared-with-me/life-stories/${item.inviteId}`)}
                          >
                            Read
                          </Button>
                        </div>
                      </Card>
                    </Col>
                  ))}
                </Row>
              )}
            </Space>
          </section>
        </div>
      </ContainerComponent>

      <Footer />
    </div>
  );
}

export default SharedWithMe;
